// ---------------------------------------------------------------------------
// Tariff CHAIN INTEGRITY — does a tariff's own set of versions mesh, gap-free
// and overlap-free? And if a new/edited version's dates were saved, would
// THEY leave a hole?
//
// WHY THIS EXISTS
// A tariff's versions are chained per financial year. The union has to cover
// every day, gap-free — "v1 1 Jan 2024 → 31 Dec 2024, then v2 3 March 2025 →
// 31 Dec 2025" leaves 1 Jan – 2 Mar 2025 untariffed, and nothing stopped that
// before TV-4. `test321` is the one live tariff this already happened to: a
// 95-day gap, 2026-05-01 → 2026-08-04.
//
// Two jobs, both here because they are the same underlying question asked
// twice — design spec §5.4:
//
//   PREVENTION  `checkCandidateMesh` — before a create/edit is submitted,
//               would ITS dates leave a gap or overlap touching themselves?
//               Mirrors the real gate, `workers/billing-api`'s
//               `evaluateChainMeshGuard` (chainMeshGuard.ts), so the
//               server's rejection is never the first the operator hears of.
//   DETECTION   `summariseChainIntegrity` — does a tariff's EXISTING chain
//               already have a gap or overlap anywhere? Feeds the
//               Tariff Management rolled-up badge, extending the pattern
//               `tariffEffectivity.ts` established (see that file's header)
//               rather than inventing a second one.
//
// ─────────────────────────────────────────────────────────────────────────
// THE AUTHORITATIVE RULE LIVES IN workers/shared/billing-core/coverage.ts
// ─────────────────────────────────────────────────────────────────────────
// `findChainGaps` / `findChainOverlaps` / `checkChainMesh` there are the real
// answer, used by `billing-api`'s create/edit guard. This SPA is browser code
// and cannot import Worker source, so — exactly like `tariffEffectivity.ts`
// mirrors `toDateKey` — the rule is MIRRORED here. `toDateKey` and
// `nextDayKey` below are character-for-character coverage.ts:168 and :189.
//
// The gap-finding ALGORITHM here is deliberately NOT a port of
// `resolveVersionCoverage`'s cursor walk (that is written for arbitrary
// billing periods and version-boundary splitting, neither of which applies
// to "does this chain mesh"). It is a standard sorted-interval merge instead:
// sort active windows by start, grow a running cluster end, and record a gap
// whenever the next window starts after the current cluster closes. Verified
// against the same `test321` and `Ruraflex >500kVA` shapes coverage.test.ts
// pins (see tariffChainIntegrity.test.ts) — same answer, simpler mechanism,
// because this module only ever needs "is there a hole", never "which
// version prices which day".
// ---------------------------------------------------------------------------

/** The only fields chain-integrity reads — same permissive shape as `EffectiveWindow`. */
export interface ChainWindow {
  id: string;
  effective_from: string;
  effective_to?: string | null;
  is_active?: number | boolean | null;
}

/** A half-open run of days, `[start, end)`. */
export interface DateSpan {
  start: string;
  end: string;
}

/** Two active versions whose day-ranges share at least one day. */
export interface VersionOverlap {
  aVersionId: string;
  bVersionId: string;
  start: string;
  /** `null` when BOTH versions are open-ended. */
  end: string | null;
}

/** CHARACTER-FOR-CHARACTER coverage.ts:168 / tariffEffectivity.ts:126. */
export function toDateKey(value: string): string {
  const t = value.indexOf('T');
  if (t === 10) return value.slice(0, 10);
  const sp = value.indexOf(' ');
  if (sp === 10) return value.slice(0, 10);
  return value;
}

/** CHARACTER-FOR-CHARACTER coverage.ts:185. UTC explicitly — see that file's note. */
export function nextDayKey(inclusiveLastDay: string): string {
  const key = toDateKey(inclusiveLastDay);
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** Is this row active? Absent/null means active — mirrors coverage.ts:196. */
function isActive(version: ChainWindow): boolean {
  const flag = version.is_active;
  if (flag === undefined || flag === null) return true;
  return flag !== 0 && flag !== false;
}

/** A version reduced to the half-open day range it claims. `end === null` is open-ended. */
interface Window {
  id: string;
  start: string;
  end: string | null;
}

function toWindow(v: ChainWindow): Window {
  return {
    id: v.id,
    start: toDateKey(v.effective_from),
    end: v.effective_to === null || v.effective_to === undefined ? null : nextDayKey(v.effective_to),
  };
}

/**
 * Every day within the span of `versions` (earliest start to latest end, or
 * forever if any active version is open-ended) that no ACTIVE version
 * covers. `[]` for zero/one version or a chain that already meshes.
 *
 * Sorted-interval merge: grow a running cluster end across active windows in
 * start order, and record a gap whenever the next window starts after the
 * current cluster has already closed. An open-ended window (`end === null`)
 * closes the cluster forever — nothing after it can create a gap.
 */
export function findChainGaps(versions: readonly ChainWindow[]): DateSpan[] {
  const windows = versions
    .filter((v) => isActive(v))
    .map(toWindow)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

  if (windows.length === 0) return [];

  const gaps: DateSpan[] = [];
  let clusterEnd: string | null = windows[0].end;

  for (let i = 1; i < windows.length; i++) {
    const w = windows[i];
    if (clusterEnd === null) continue; // cluster already extends forever

    if (w.start > clusterEnd) {
      gaps.push({ start: clusterEnd, end: w.start });
      clusterEnd = w.end;
    } else if (w.end === null || w.end > clusterEnd) {
      clusterEnd = w.end;
    }
  }

  return gaps;
}

function rangeEndKey(end: string | null): string {
  return end === null ? '9999-12-31' : end;
}

function rangesIntersect(aStart: string, aEnd: string | null, bStart: string, bEnd: string | null): boolean {
  return aStart < rangeEndKey(bEnd) && bStart < rangeEndKey(aEnd);
}

function overlapEndKey(aEnd: string | null, bEnd: string | null): string | null {
  if (aEnd === null && bEnd === null) return null;
  if (aEnd === null) return bEnd;
  if (bEnd === null) return aEnd;
  return aEnd < bEnd ? aEnd : bEnd;
}

/** Every pair of ACTIVE versions in `versions` whose day-ranges overlap. O(n²); a tariff's own version count is single digits. */
export function findChainOverlaps(versions: readonly ChainWindow[]): VersionOverlap[] {
  const windows = versions.filter((v) => isActive(v)).map(toWindow);
  const overlaps: VersionOverlap[] = [];

  for (let i = 0; i < windows.length; i++) {
    for (let j = i + 1; j < windows.length; j++) {
      const a = windows[i];
      const b = windows[j];
      if (!rangesIntersect(a.start, a.end, b.start, b.end)) continue;
      overlaps.push({
        aVersionId: a.id,
        bVersionId: b.id,
        start: a.start > b.start ? a.start : b.start,
        end: overlapEndKey(a.end, b.end),
      });
    }
  }

  return overlaps;
}

/** What creating or editing one version would do to its tariff's chain — the form's pre-submit check. */
export interface ChainMeshCheck {
  ok: boolean;
  /** Gaps touching the CANDIDATE's own boundary only — never a pre-existing gap elsewhere in the chain. */
  gaps: DateSpan[];
  overlaps: VersionOverlap[];
}

/**
 * Would `candidate` — the version form's current values — leave a gap or
 * overlap touching its OWN dates, against `others` (the tariff's other
 * versions; the row being edited may be included or excluded, it is
 * excluded here by id either way)?
 *
 * Scoped to the candidate deliberately, same reasoning as billing-core's
 * `checkChainMesh`: `test321` already has an unrelated gap, and opening its
 * "add version" or "edit" modal on a DIFFERENT version must not show a
 * warning about a hole that version has nothing to do with.
 */
export function checkCandidateMesh(
  candidateId: string | null,
  candidate: { effective_from: string; effective_to?: string | null },
  others: readonly ChainWindow[],
): ChainMeshCheck {
  if (!candidate.effective_from) return { ok: true, gaps: [], overlaps: [] };

  const activeOthers = others.filter((v) => isActive(v) && v.id !== candidateId);
  const candStart = toDateKey(candidate.effective_from);
  const candToRaw = candidate.effective_to ?? null;
  const candEnd = candToRaw === null ? null : nextDayKey(candToRaw);

  const overlaps: VersionOverlap[] = [];
  for (const o of activeOthers) {
    const oW = toWindow(o);
    if (!rangesIntersect(candStart, candEnd, oW.start, oW.end)) continue;
    overlaps.push({
      aVersionId: candidateId ?? '(new version)',
      bVersionId: o.id,
      start: candStart > oW.start ? candStart : oW.start,
      end: overlapEndKey(candEnd, oW.end),
    });
  }

  const combined: ChainWindow[] = [
    ...activeOthers,
    { id: candidateId ?? '__candidate__', effective_from: candidate.effective_from, effective_to: candToRaw, is_active: 1 },
  ];
  const gaps = findChainGaps(combined).filter(
    (g) => g.end === candStart || (candEnd !== null && g.start === candEnd),
  );

  return { ok: overlaps.length === 0 && gaps.length === 0, gaps, overlaps };
}

/**
 * The version whose coverage currently reaches furthest into the future,
 * `effective_to + 1 day` — the default for a NEW version's `effective_from`,
 * so meshing is what happens when nobody thinks about it (design spec §5.4).
 *
 * `null` when there is no active, bounded version to continue from — the
 * tariff's very first version, or a chain that is already open-ended (adding
 * another version after an open end would only overlap it; there is no
 * sensible date to suggest).
 */
export function defaultNextEffectiveFrom(versions: readonly ChainWindow[]): string | null {
  let latestEnd: string | null = null;

  for (const v of versions) {
    if (!isActive(v)) continue;
    if (v.effective_to === null || v.effective_to === undefined) continue;
    const end = nextDayKey(v.effective_to);
    if (latestEnd === null || end > latestEnd) latestEnd = end;
  }

  return latestEnd;
}

/** Whether a tariff's own chain has a problem, and the actionable span(s). */
export interface ChainIntegritySummary {
  hasIssue: boolean;
  gaps: DateSpan[];
  overlaps: VersionOverlap[];
}

/** Roll a tariff's versions up into one chain-integrity answer — the tariff-card badge's input. */
export function summariseChainIntegrity(versions: readonly ChainWindow[]): ChainIntegritySummary {
  const gaps = findChainGaps(versions);
  const overlaps = findChainOverlaps(versions);
  return { hasIssue: gaps.length > 0 || overlaps.length > 0, gaps, overlaps };
}

function days(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

function wholeDays(span: DateSpan): number {
  const a = Date.parse(`${span.start}T00:00:00Z`);
  const b = Date.parse(`${span.end}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/**
 * The tariff-CARD badge label, or `null` when the chain is fine — silent by
 * design, same rule `tariffEffectivityLabel` follows: a warning the common
 * case also carries stops being read.
 *
 * Deliberately SHORT — a day count, not the span itself. `Badge` is
 * `whitespace-nowrap` (CLAUDE.md §7.1) and this badge sits in the same
 * `flex-wrap` card-header row `tariffEffectivityLabel` already puts
 * "Expires in 45 days" into; the full span
 * ("no tariff 2026-05-01 – 2026-08-03") would be ~4x longer than that
 * existing widest label ("No version in effect", 21 chars) and risks
 * exceeding the ~341px mobile gutter budget on its own. The exact span the
 * design spec asks for ("no tariff 1 Jan – 2 Mar 2025 is actionable") is
 * `chainIntegrityDetailLabel`, rendered as ordinary wrappable text in the
 * expanded tariff panel — one click past this badge, never further.
 */
export function chainIntegrityBadgeLabel(summary: ChainIntegritySummary): string | null {
  if (summary.gaps.length > 0) {
    return `Gap: ${days(wholeDays(summary.gaps[0]))}`;
  }
  if (summary.overlaps.length > 0) {
    const o = summary.overlaps[0];
    return o.end === null ? 'Overlap' : `Overlap: ${days(wholeDays({ start: o.start, end: o.end }))}`;
  }
  return null;
}

/**
 * The full, actionable chain-integrity line — names the exact span, for the
 * expanded tariff panel where ordinary text can wrap. Design spec: "no
 * tariff 1 Jan – 2 Mar 2025 is actionable; 'chain gap' is not."
 *
 * `null` when the chain is fine. When there is more than one problem, the
 * FIRST gap takes priority over an overlap — gaps are far more common (10 of
 * 11 live chains mesh, the 11th has exactly one gap; zero overlaps exist
 * today) — but `hasIssue` and the full `gaps`/`overlaps` arrays are always
 * available for a caller that wants every one of them.
 */
export function chainIntegrityDetailLabel(summary: ChainIntegritySummary): string | null {
  if (summary.gaps.length > 0) {
    const g = summary.gaps[0];
    return `No tariff version covers ${g.start} – ${previousDay(g.end)} (${days(wholeDays(g))}).`;
  }
  if (summary.overlaps.length > 0) {
    const o = summary.overlaps[0];
    return `Two versions both cover ${o.start}${o.end ? ` – ${previousDay(o.end)}` : ' onward'} — ambiguous pricing.`;
  }
  return null;
}

/** The inclusive last day of an exclusive-end span, for operator-facing copy. `2026-08-04` (exclusive) reads as "2 Mar 2025", not the day nothing happens. */
function previousDay(exclusiveEnd: string): string {
  const d = new Date(`${exclusiveEnd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
